/**
 * Web Sentry error-filtering constants.
 *
 * The decision logic — crash safeguard, DOMException-safe name/message reading,
 * suppression — lives in `@ergon-ai/utils` (`createErrorFilter`). This module
 * only declares the web filtered patterns and binds them, then re-exports the
 * shared helpers so the logger and the Sentry `beforeSend` hooks keep their
 * existing imports. Web filters only the shared base; native-only patterns
 * (cold-start malformed auth token, RevenueCat purchase restriction, etc.) do
 * not apply here.
 *
 * Imported via the `@ergon-ai/utils/errorFiltering` subpath (not the package
 * root) on purpose: pulling the whole `@ergon-ai/utils` barrel into the
 * logger's module-load path partial-initialized under app bundlers and left
 * these exports `undefined`. Keep the subpath.
 */
import {
  BASE_FILTERED_ERROR_NAMES,
  BASE_FILTERED_ERROR_PATTERNS,
  createErrorFilter,
} from "@ergon-ai/utils/errorFiltering";

export {
  BREADCRUMB_RATE_LIMIT,
  categorizeError,
  getErrorNameAndMessage,
  isCrashLevelEvent,
} from "@ergon-ai/utils/errorFiltering";
export type { ErrorCategory } from "@ergon-ai/utils/errorFiltering";

/** Error class names filtered on web (shared base: validation classes + AbortError). */
export const FILTERED_ERROR_NAMES = BASE_FILTERED_ERROR_NAMES;

/** Message patterns filtered on web (shared base only). */
export const FILTERED_ERROR_PATTERNS = BASE_FILTERED_ERROR_PATTERNS;

const filter = createErrorFilter({
  names: FILTERED_ERROR_NAMES,
  patterns: FILTERED_ERROR_PATTERNS,
});

export const {
  isFilteredErrorName,
  isFilteredErrorMessage,
  isUserFacingError,
  shouldSuppressEvent,
} = filter;
